import React, { useState, useRef, useEffect } from 'react';
import { PasswordGateProps } from '@/types/auth';

export const PasswordGate: React.FC<PasswordGateProps> = ({
  onSuccess,
  title = '血壓健康管理檔案',
  subtitle = 'CONFIDENTIAL CLINICAL RECORD • 存取權限驗證',
  defaultRememberMe = true,
}) => {
  const [digits, setDigits] = useState<string[]>(['', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(defaultRememberMe);
  const [showKeypad, setShowKeypad] = useState<boolean>(true);

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Auto focus first input on mount
  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  const handleDigitChange = (index: number, value: string) => {
    setError(null);
    const cleanVal = value.replace(/\D/g, '').slice(-1);

    const newDigits = [...digits];
    newDigits[index] = cleanVal;
    setDigits(newDigits);

    // If character entered, advance focus
    if (cleanVal && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // If all 4 filled, trigger verification
    if (cleanVal && index === 3 && newDigits.every((d) => d !== '')) {
      verifyCode(newDigits.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        // Move back and clear previous
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        setDigits(newDigits);
        inputRefs[index - 1].current?.focus();
      } else {
        const newDigits = [...digits];
        newDigits[index] = '';
        setDigits(newDigits);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs[index - 1].current?.focus();
    } else if (e.key === 'ArrowRight' && index < 3) {
      inputRefs[index + 1].current?.focus();
    } else if (e.key === 'Enter') {
      const code = digits.join('');
      if (code.length === 4) {
        verifyCode(code);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const numbersOnly = pastedData.replace(/\D/g, '').slice(0, 4);

    if (numbersOnly.length > 0) {
      const newDigits = ['', '', '', ''];
      for (let i = 0; i < numbersOnly.length; i++) {
        newDigits[i] = numbersOnly[i];
      }
      setDigits(newDigits);

      if (numbersOnly.length === 4) {
        verifyCode(numbersOnly);
      } else {
        const nextIndex = Math.min(numbersOnly.length, 3);
        inputRefs[nextIndex].current?.focus();
      }
    }
  };

  const handleKeypadPress = (num: string) => {
    setError(null);
    const nextEmptyIndex = digits.findIndex((d) => d === '');
    if (nextEmptyIndex !== -1) {
      const newDigits = [...digits];
      newDigits[nextEmptyIndex] = num;
      setDigits(newDigits);

      if (nextEmptyIndex < 3) {
        inputRefs[nextEmptyIndex + 1].current?.focus();
      } else {
        verifyCode(newDigits.join(''));
      }
    }
  };

  const handleKeypadBackspace = () => {
    setError(null);
    let targetIndex = -1;
    for (let i = 3; i >= 0; i--) {
      if (digits[i] !== '') {
        targetIndex = i;
        break;
      }
    }
    if (targetIndex !== -1) {
      const newDigits = [...digits];
      newDigits[targetIndex] = '';
      setDigits(newDigits);
      inputRefs[targetIndex].current?.focus();
    }
  };

  const handleKeypadClear = () => {
    setError(null);
    setDigits(['', '', '', '']);
    inputRefs[0].current?.focus();
  };

  const verifyCode = async (code: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // First attempt to call the backend API endpoint if reachable
      let token = '';
      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: code }),
        });
        const data = await response.json();
        if (response.ok && data.success && data.token) {
          token = data.token;
        } else if (!response.ok || !data.success) {
          throw new Error(data.error || '密碼錯誤');
        }
      } catch (fetchErr) {
        // Fallback local verification: Passcode requirement is 9015
        if (code === '9015') {
          token = `bp_local_${Date.now()}_9015`;
        } else {
          throw new Error('存取密碼不正確，請重新輸入');
        }
      }

      // Success branch
      setIsSuccess(true);
      if (rememberMe) {
        localStorage.setItem('bp_auth_token', token);
        localStorage.setItem('bp_auth_saved_passcode', code);
      } else {
        sessionStorage.setItem('bp_auth_token', token);
      }

      setTimeout(() => {
        onSuccess(token);
      }, 500);
    } catch (err: any) {
      setError(err.message || '存取密碼不正確，請重新輸入');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      setDigits(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper-100 p-4 sm:p-6 select-none">
      {/* Editorial Confidential Archive Card */}
      <div
        className={`
          w-full max-w-md bg-white border border-paper-400 p-6 sm:p-8 rounded-sm
          shadow-[4px_4px_0px_0px_rgba(28,25,23,0.12)] relative
          ${isShaking ? 'animate-shake' : ''}
        `}
      >
        {/* Top Confidential Stamp Label */}
        <div className="flex items-center justify-between text-[11px] font-serif uppercase tracking-widest text-ink-500 pb-2 border-b border-paper-300">
          <span>ARCHIVE FILE NO. #9015</span>
          <span className="inline-block border border-red-800 text-red-900 px-1.5 py-0.2 rounded-xs font-bold text-[10px] rotate-[-1deg]">
            RESTRICTED ACCESS
          </span>
        </div>

        {/* Masthead Header */}
        <div className="ruled-double my-4 text-center">
          <h1 className="font-serif text-xl sm:text-2xl font-bold tracking-wider text-ink-900 uppercase">
            {title}
          </h1>
          <p className="text-xs text-ink-500 font-serif tracking-widest mt-1">
            {subtitle}
          </p>
        </div>

        <p className="text-center text-xs text-ink-700 font-serif mb-6 leading-relaxed">
          此系統受存取權限保護，請輸入 4 位數授權密碼以查閱或登記臨床血壓紀錄。
        </p>

        {/* 4-Digit Input Boxes */}
        <div className="flex justify-center items-center gap-3 sm:gap-4 my-6">
          {digits.map((digit, idx) => (
            <input
              key={idx}
              ref={inputRefs[idx]}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              disabled={isLoading || isSuccess}
              onChange={(e) => handleDigitChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              onPaste={handlePaste}
              className={`
                w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl sm:text-3xl font-mono font-bold
                bg-paper-200 border-2 rounded-xs transition-all duration-150 outline-none
                ${
                  digit
                    ? 'border-ink-900 bg-white text-ink-900'
                    : 'border-paper-400 text-ink-700 focus:border-ink-800 focus:bg-white'
                }
                ${error ? 'border-red-600 bg-red-50/40 text-red-900' : ''}
                ${isSuccess ? 'border-emerald-700 bg-emerald-50 text-emerald-900' : ''}
              `}
              aria-label={`密碼第 ${idx + 1} 位`}
            />
          ))}
        </div>

        {/* Feedback Area: Error Stamp / Success Stamp / Loading */}
        <div className="min-h-[36px] flex items-center justify-center my-3 text-center">
          {isLoading && (
            <div className="inline-flex items-center gap-2 text-xs font-serif text-ink-700">
              <span className="w-3.5 h-3.5 border-2 border-ink-700 border-t-transparent rounded-full animate-spin" />
              <span>驗證存取憑證中...</span>
            </div>
          )}

          {error && !isLoading && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 border border-red-700 bg-red-50 text-red-900 text-xs font-serif font-bold tracking-wider rounded-xs rotate-[-0.5deg]">
              <span>✖ {error}</span>
            </div>
          )}

          {isSuccess && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 border-2 border-emerald-700 bg-emerald-50 text-emerald-900 text-xs font-serif font-bold tracking-wider rounded-xs animate-stamp-in">
              <span>✔ 授權通過 • 正在載入檔案...</span>
            </div>
          )}
        </div>

        {/* Remember Me Checkbox */}
        <div className="flex items-center justify-center gap-2 my-4 text-xs text-ink-700 font-serif">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="rounded-xs border-paper-400 text-ink-900 focus:ring-0 focus:ring-offset-0 w-3.5 h-3.5"
            />
            <span>在此裝置保持登入狀態</span>
          </label>
        </div>

        {/* Numeric On-Screen Keypad for Fast Mobile Entry */}
        {showKeypad && (
          <div className="mt-6 pt-4 border-t border-dashed border-paper-300">
            <div className="grid grid-cols-3 gap-2 max-w-[280px] mx-auto">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => handleKeypadPress(num)}
                  disabled={isLoading || isSuccess}
                  className="
                    h-11 bg-paper-100 hover:bg-paper-200 active:bg-paper-300
                    border border-paper-400 text-ink-900 font-mono font-bold text-lg
                    rounded-xs shadow-paper-sm transition-transform active:translate-y-0.5
                  "
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                onClick={handleKeypadClear}
                disabled={isLoading || isSuccess}
                className="
                  h-11 bg-paper-200 hover:bg-paper-300 active:bg-paper-400
                  border border-paper-400 text-ink-700 font-serif text-xs font-bold
                  rounded-xs shadow-paper-sm transition-transform active:translate-y-0.5
                "
              >
                清除
              </button>
              <button
                type="button"
                onClick={() => handleKeypadPress('0')}
                disabled={isLoading || isSuccess}
                className="
                  h-11 bg-paper-100 hover:bg-paper-200 active:bg-paper-300
                  border border-paper-400 text-ink-900 font-mono font-bold text-lg
                  rounded-xs shadow-paper-sm transition-transform active:translate-y-0.5
                "
              >
                0
              </button>
              <button
                type="button"
                onClick={handleKeypadBackspace}
                disabled={isLoading || isSuccess}
                className="
                  h-11 bg-paper-200 hover:bg-paper-300 active:bg-paper-400
                  border border-paper-400 text-ink-700 font-serif text-xs font-bold
                  rounded-xs shadow-paper-sm transition-transform active:translate-y-0.5
                "
              >
                ⌫ 倒退
              </button>
            </div>
          </div>
        )}

        {/* Toggle Keypad visibility */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setShowKeypad(!showKeypad)}
            className="text-[11px] text-ink-500 hover:text-ink-900 font-serif underline"
          >
            {showKeypad ? '隱藏數字鍵盤' : '顯示數字鍵盤'}
          </button>
        </div>

        {/* Ledger Document Footer */}
        <div className="mt-6 pt-3 border-t border-paper-300 flex items-center justify-between text-[10px] font-serif text-ink-500">
          <span>STANDARD SYSTEM PASSCODE: 9015</span>
          <span>SECURED MEDICAL ARCHIVE</span>
        </div>
      </div>
    </div>
  );
};
